import type { FieldValues, Path, Control } from 'react-hook-form'

import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from 'src/shared/components/ui/forms/form'
import { PriceRangeSlider } from 'src/shared/components/ui/sliders/price-range-slider'
import { cn } from 'src/shared/utils/cn'
import { formatCurrency } from 'src/shared/utils/format-currency'

interface PriceRangeFormFieldProps<T extends FieldValues> {
  name: Path<T>
  label: string
  description?: string
  className?: string
  fieldClassName?: string
  disabled?: boolean
  required?: boolean
  control?: Control<T>
  min?: number
  max?: number
  step?: number
  inputClassName?: string
  trackClassName?: string
  labelClassName?: string
}

export function PriceRangeFormField<T extends FieldValues>({
  name,
  label,
  description,
  className,
  fieldClassName,
  inputClassName,
  trackClassName,
  labelClassName,
  disabled,
  required,
  control,
  min = 0,
  max = 1500000,
  step = 10000,
}: PriceRangeFormFieldProps<T>) {
  if (required) {
    label = `${label} *`
  }

  const formatMaxValue = (value: number) => {
    if (value >= max) {
      return `${formatCurrency(value).replace('.00', '')}+`
    }
    return undefined
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('w-full', className)}>
          <FormLabel className={labelClassName}>{label}</FormLabel>
          <FormControl>
            <PriceRangeSlider
              className={fieldClassName}
              disabled={disabled}
              field={{
                value: [field.value?.min ?? min, field.value?.max ?? max],
                onChange: (value) => {
                  field.onChange({ min: value[0], max: value[1] })
                },
              }}
              min={min}
              max={max}
              step={step}
              formatMaxValue={formatMaxValue}
              inputClassName={inputClassName}
              trackClassName={trackClassName}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
